import { createTheme } from '@mui/material';

export const appTheme = createTheme({
  palette: {
    primary: { main: '#E62830', dark: '#CC1F27', light: '#FEF1F2', contrastText: '#FFFFFF' },
    secondary: { main: '#2E7FC4', dark: '#1F69A8', light: '#EAF2FA' },
    success: { main: '#0E9F6E', dark: '#048258', light: '#ECFDF5' },
    warning: { main: '#D97706', light: '#FFFBEB' },
    background: { default: '#FAFBFC', paper: '#FFFFFF' },
    text: { primary: '#0B0D10', secondary: '#4A5159' },
    divider: '#E6E8EC',
  },
  shape: { borderRadius: 12 },
  typography: {
    fontFamily: 'Manrope, system-ui, -apple-system, "Segoe UI", sans-serif',
    h1: {
      fontSize: 'clamp(2.25rem, 6vw, 3.5rem)',
      fontWeight: 800,
      lineHeight: 1.1,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontSize: 'clamp(1.75rem, 4.5vw, 2.5rem)',
      fontWeight: 800,
      lineHeight: 1.1,
      letterSpacing: '-0.02em',
    },
    h3: { fontSize: '1.5rem', fontWeight: 700, lineHeight: 1.25, letterSpacing: '-0.01em' },
    h4: { fontSize: '1.25rem', fontWeight: 700 },
    h5: { fontSize: '1.125rem', fontWeight: 700 },
    button: { fontWeight: 700, textTransform: 'none' },
  },
  components: {
    MuiCssBaseline: { styleOverrides: { body: { backgroundColor: '#FAFBFC' } } },
    // Stack spaces its children with margins and resets every child's margin to do it, which
    // wipes the negative left margin a Grid container relies on: any grid that is not the first
    // child of a Stack ended up shifted by its spacing. Gap spaces without touching margins.
    MuiStack: { defaultProps: { useFlexGap: true } },
    MuiCard: {
      styleOverrides: {
        root: {
          border: '1px solid #E6E8EC',
          boxShadow: '0 1px 3px rgba(11,13,16,.06)',
          borderRadius: 12,
        },
      },
    },
    MuiCardContent: {
      styleOverrides: { root: { padding: 24, '&:last-child': { paddingBottom: 24 } } },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          padding: '12px 20px',
          transition:
            'background 120ms ease-out, box-shadow 200ms ease-out, transform 120ms ease-out',
          '&:active': { transform: 'scale(.98)' },
        },
        containedPrimary: {
          boxShadow: '0 8px 24px rgba(230,40,48,.22)',
          '&:hover': { backgroundColor: '#CC1F27' },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#E62830',
            boxShadow: '0 0 0 4px rgba(230,40,48,.12)',
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        // The focus ring above is a box-shadow on the notched outline, and a shadow is
        // painted around the whole box: unlike the border, it does not follow the notch cut
        // for the label. So the soft red passes behind the floating label and hurts its
        // legibility. Painting the shrunk label on the surface colour restores the gap the
        // notch was meant to provide, and keeps the design system focus ring intact.
        outlined: {
          '&.MuiInputLabel-shrink': {
            backgroundColor: '#FFFFFF',
            paddingLeft: 4,
            paddingRight: 4,
          },
        },
      },
    },
    MuiChip: { styleOverrides: { root: { borderRadius: 999, fontWeight: 700 } } },
    // MUI draws the line under each row by lightening the divider, which on white all but
    // disappears. A visible slate line lets the eye follow a tall row to its actions (#111).
    MuiTableCell: { styleOverrides: { root: { borderBottomColor: '#CDD2D8' } } },
  },
});
